import React, { useState, useRef, useEffect } from "react";
import Papa from "papaparse";
import { toast } from "react-toastify";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import {
  FaArrowLeft,
  FaArrowRight,
  FaUndoAlt,
  FaRedoAlt,
  FaMagic,
} from "react-icons/fa";
import { BASE_URL } from "../../_helper/constant/constant";
import { ApiEndPoint } from "../../_helper/constant/api-types";

/* ==========================
   CSV Upload Component
   --------------------------
   This component parses a CSV file, validates required columns,
   and passes the parsed data back via onUpload.
   ========================== */
export const UploadBatchModal = ({ onClose, onUpload, isPublished }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
  };

  const handleUpload = () => {
    if (!csvFile) {
      toast.error("Please select a CSV file to upload.");
      return;
    }
    if (isPublished) {
      toast.warn("Cannot modify a published receipt.");
      return;
    }
    setIsUploading(true);
    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        setIsUploading(false);
        const data = results.data;
        try {
          // Define required CSV columns
          const requiredFields = [
            "item_name",
            "batchNumber",
            "inwardQuantity",
            "pricePerUnit",
            "mrp",
            "receivedDate",
            "expiryDate",
          ];

          const parsedItems = data.map((row, index) => {
            // Validate required fields
            for (let field of requiredFields) {
              if (!row[field] || row[field].toString().trim() === "") {
                toast.error(
                  `Row ${index + 2}: Missing required field "${field}".`
                );
                throw new Error(
                  `Missing required field ${field} in row ${index + 2}`
                );
              }
            }

            const inwardQuantity = Number(row.inwardQuantity);
            const pricePerUnit = Number(row.pricePerUnit);
            const mrp = Number(row.mrp);
            const cgstRate = row.cgstRate ? Number(row.cgstRate) : 6;
            const sgstRate = row.sgstRate ? Number(row.sgstRate) : 6;
            const igstRate = row.igstRate ? Number(row.igstRate) : 0;

            const taxableValue = inwardQuantity * pricePerUnit;
            const cgst = (taxableValue * cgstRate) / 100;
            const sgst = (taxableValue * sgstRate) / 100;
            const igst = (taxableValue * igstRate) / 100;
            const totalGST = cgst + sgst + igst;
            const totalAmount = taxableValue + totalGST;
            const profitPerUnit = mrp - pricePerUnit;
            const profitMargin =
              pricePerUnit > 0 ? (profitPerUnit / pricePerUnit) * 100 : 0;

            return {
              item: {
                item_name: row.item_name,
                item_id: row.item_id ? row.item_id : null,
              },
              hsn: row.hsn,
              batchNumber: row.batchNumber,
              inwardQuantity,
              pricePerUnit,
              mrp,
              price: mrp,
              unitOfMeasure: row.unitOfMeasure || "Units",
              cgstRate,
              sgstRate,
              igstRate,
              taxableValue,
              cgst,
              sgst,
              igst,
              totalGST,
              totalAmount,
              profitPerUnit: Number(profitPerUnit.toFixed(2)),
              profitMargin: Number(profitMargin.toFixed(2)),
              receivedDate: row.receivedDate,
              expiryDate: row.expiryDate,
            };
          });

          onUpload(parsedItems);
          onClose();
        } catch (error) {
          console.error("Error processing CSV:", error);
        }
      },
      error: function (err) {
        setIsUploading(false);
        toast.error("Error parsing CSV file.");
        console.error(err);
      },
    });
  };

  return (
    <div className="p-4">
      <h3 className="text-2xl font-semibold mb-4">Upload Batch (CSV)</h3>
      <input
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        className="w-full mb-4"
      />
      <div className="flex justify-end space-x-4">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          onClick={handleUpload}
          disabled={isUploading}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          {isUploading ? "Uploading..." : "Upload"}
        </button>
      </div>
    </div>
  );
};

/* ==========================
   Utility Function
   --------------------------
   Converts a data URL to a Blob.
   ========================== */
const dataURLtoBlob = (dataurl) => {
  const arr = dataurl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

/* ==========================
   Document Scanner Component
   --------------------------
   This component is used for both Gallery Upload and Smart Scan.
   When the "mode" prop is set to "smart", the file input
   includes the capture attribute so that mobile users can take a photo.
   It allows cropping, rotating, and enhancing of images,
   and displays an OCR results table after processing.
   ========================== */
export const DocumentScanner = ({ mode }) => {
  // mode: "gallery" or "smart"
  const [images, setImages] = useState([]);
  const [processedImages, setProcessedImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showFinalUI, setShowFinalUI] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ocrData, setOcrData] = useState([]);
  const cropperRef = useRef(null);

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files) {
      const fileArray = Array.from(files);
      Promise.all(
        fileArray.map(
          (file) =>
            new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.readAsDataURL(file);
            })
        )
      ).then((dataURLs) => {
        setImages(dataURLs);
        setProcessedImages(Array(dataURLs.length).fill(null));
        setCurrentIndex(0);
        setShowFinalUI(false);
        setOcrData([]);
      });
    }
  };

  useEffect(() => {
    if (cropperRef.current && images.length > 0 && !showFinalUI) {
      const cropper = cropperRef.current.cropper;
      cropper.reset();
    }
  }, [currentIndex, images, showFinalUI]);

  const getCroppedCanvas = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      return cropper.getCroppedCanvas();
    }
    return null;
  };

  const rotateLeft = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) cropper.rotate(-90);
  };
  const rotateRight = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) cropper.rotate(90);
  };

  const handleAutoCrop = () => {
    if (!window.cv) {
      alert("OpenCV is not loaded yet!");
      return;
    }
    // Here you could integrate AI-based or OpenCV auto-cropping logic
    alert("Auto crop applied for current image.");
  };

  const applyUnsharpMask = (canvas, amount = 2.0, blurRadius = 2) => {
    const ctx = canvas.getContext("2d");
    const originalData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    const blurCanvas = document.createElement("canvas");
    blurCanvas.width = canvas.width;
    blurCanvas.height = canvas.height;
    const blurCtx = blurCanvas.getContext("2d");
    blurCtx.filter = `blur(${blurRadius}px)`;
    blurCtx.drawImage(canvas, 0, 0);
    const blurredData = blurCtx.getImageData(0, 0, canvas.width, canvas.height);

    const outputData = ctx.createImageData(canvas.width, canvas.height);
    const len = originalData.data.length;
    for (let i = 0; i < len; i += 4) {
      outputData.data[i] = Math.min(
        255,
        Math.max(
          0,
          originalData.data[i] +
            amount * (originalData.data[i] - blurredData.data[i])
        )
      );
      outputData.data[i + 1] = Math.min(
        255,
        Math.max(
          0,
          originalData.data[i + 1] +
            amount * (originalData.data[i + 1] - blurredData.data[i + 1])
        )
      );
      outputData.data[i + 2] = Math.min(
        255,
        Math.max(
          0,
          originalData.data[i + 2] +
            amount * (originalData.data[i + 2] - blurredData.data[i + 2])
        )
      );
      outputData.data[i + 3] = originalData.data[i + 3];
    }
    ctx.putImageData(outputData, 0, 0);
  };

  const handleEnhance = () => {
    const croppedCanvas = getCroppedCanvas();
    if (!croppedCanvas) {
      alert("Please crop the image first.");
      return;
    }
    // Upscale for improved resolution.
    const scaleFactor = 2;
    const enhancedCanvas = document.createElement("canvas");
    enhancedCanvas.width = croppedCanvas.width * scaleFactor;
    enhancedCanvas.height = croppedCanvas.height * scaleFactor;
    const ctx = enhancedCanvas.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    // Apply filters for clear appearance.
    ctx.filter = "contrast(120%) brightness(130%) saturate(100%)";
    ctx.drawImage(
      croppedCanvas,
      0,
      0,
      enhancedCanvas.width,
      enhancedCanvas.height
    );
    applyUnsharpMask(enhancedCanvas, 2.0, 2);
    const enhancedDataUrl = enhancedCanvas.toDataURL();

    setProcessedImages((prev) => {
      const newArr = [...prev];
      newArr[currentIndex] = enhancedDataUrl;
      return newArr;
    });

    // If not the last image, move to the next; otherwise, show final UI.
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setShowFinalUI(true);
    }
  };

  const goPrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };
  const goNext = () => {
    if (currentIndex < images.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const handleBackToCarousel = () => {
    setShowFinalUI(false);
    setCurrentIndex(images.length - 1);
    setOcrData([]);
  };

  const handleDone = async () => {
    setUploading(true);
    let combinedData = [];
    // Upload images sequentially
    for (let i = 0; i < processedImages.length; i++) {
      const imgDataUrl = processedImages[i];
      if (imgDataUrl) {
        const blob = dataURLtoBlob(imgDataUrl);
        const formData = new FormData();
        formData.append("image", blob, `image${i}.png`);
        try {
          const response = await fetch(
            `${BASE_URL}${ApiEndPoint.SMART_SCANNER}`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
              },
              body: formData,
            }
          );
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
              combinedData = combinedData.concat(result.data);
            }
          } else {
            console.error(`Upload failed for image ${i}`);
          }
        } catch (error) {
          console.error(`Error uploading image ${i}:`, error);
        }
      }
    }
    setUploading(false);
    setOcrData(combinedData);
  };

  return (
    <div className="p-4">
      <h2 className="text-2xl font-semibold mb-4">
        {mode === "smart" ? "Smart Scan (AI Powered)" : "Upload from Gallery"}
      </h2>
      <input
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        className="mb-4"
        {...(mode === "smart" ? { capture: "environment" } : {})}
      />
      {/* Carousel & Cropper */}
      {!showFinalUI && images.length > 0 && (
        <div className="relative mt-4">
          <div className="border p-2 relative">
            {processedImages[currentIndex] ? (
              <img
                src={processedImages[currentIndex]}
                alt={`Enhanced ${currentIndex + 1}`}
                className="w-full h-96 object-contain"
              />
            ) : (
              <Cropper
                src={images[currentIndex]}
                style={{ height: 400, width: "100%" }}
                initialAspectRatio={1}
                guides={true}
                ref={cropperRef}
                viewMode={1}
                background={false}
              />
            )}
            {/* Navigation icons */}
            <div
              className="absolute top-1/2 left-2 transform -translate-y-1/2 cursor-pointer"
              onClick={goPrev}
            >
              <FaArrowLeft
                size={30}
                color={currentIndex === 0 ? "grey" : "black"}
              />
            </div>
            <div
              className="absolute top-1/2 right-2 transform -translate-y-1/2 cursor-pointer"
              onClick={goNext}
            >
              <FaArrowRight
                size={30}
                color={currentIndex === images.length - 1 ? "grey" : "black"}
              />
            </div>
          </div>
          {/* Editing controls */}
          {!processedImages[currentIndex] && (
            <div className="mt-4 text-center space-x-2">
              <button
                onClick={handleAutoCrop}
                className="px-3 py-1 bg-blue-500 text-white rounded inline-flex items-center"
              >
                <FaMagic className="mr-1" /> Auto Crop
              </button>
              <button
                onClick={rotateLeft}
                className="px-3 py-1 bg-gray-500 text-white rounded inline-flex items-center"
              >
                <FaUndoAlt className="mr-1" /> Rotate Left
              </button>
              <button
                onClick={rotateRight}
                className="px-3 py-1 bg-gray-500 text-white rounded inline-flex items-center"
              >
                <FaRedoAlt className="mr-1" /> Rotate Right
              </button>
              <button
                onClick={handleEnhance}
                className="px-3 py-1 bg-green-500 text-white rounded inline-flex items-center"
              >
                Enhance & Next
              </button>
            </div>
          )}
        </div>
      )}
      {/* Final UI: Enhanced Images & OCR Table */}
      {showFinalUI && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={handleBackToCarousel}
              className="bg-none border-none"
            >
              <FaArrowLeft size={30} />
            </button>
            <h3 className="text-xl font-semibold">Enhanced Images</h3>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {processedImages.map(
              (img, idx) =>
                img && (
                  <div key={idx} className="border p-1">
                    <img
                      src={img}
                      alt={`Enhanced ${idx + 1}`}
                      className="w-36"
                    />
                  </div>
                )
            )}
          </div>
          <div className="mt-4 text-center">
            <button
              onClick={handleDone}
              className="px-4 py-2 bg-green-500 text-white rounded"
            >
              Done
            </button>
          </div>
          {uploading && (
            <div className="mt-4 text-center">
              <p>Uploading images... Please wait.</p>
            </div>
          )}
          {!uploading && ocrData.length > 0 && (
            <div className="mt-6">
              <h3 className="text-xl font-semibold mb-2">OCR Extracted Data</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="border p-2">Sr. No.</th>
                      <th className="border p-2">Item Name</th>
                      <th className="border p-2">Batch No</th>
                      <th className="border p-2">HSN</th>
                      <th className="border p-2">Inward Qty</th>
                      <th className="border p-2">Price/Unit</th>
                      <th className="border p-2">MRP</th>
                      <th className="border p-2">UoM</th>
                      <th className="border p-2">CGST</th>
                      <th className="border p-2">IGST</th>
                      <th className="border p-2">SGST</th>
                      <th className="border p-2">Received Date</th>
                      <th className="border p-2">Expiry Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ocrData.map((row, idx) => (
                      <tr key={idx}>
                        <td className="border p-2 text-center">{idx + 1}</td>
                        <td className="border p-2 text-center">
                          {row.item_name}
                        </td>
                        <td className="border p-2 text-center">
                          {row.batchNumber}
                        </td>
                        <td className="border p-2 text-center">{row.hsn}</td>
                        <td className="border p-2 text-center">
                          {row.inwardQuantity}
                        </td>
                        <td className="border p-2 text-center">
                          {row.pricePerUnit}
                        </td>
                        <td className="border p-2 text-center">{row.mrp}</td>
                        <td className="border p-2 text-center">
                          {row.unitOfMeasure}
                        </td>
                        <td className="border p-2 text-center">
                          {row.cgstRate}
                        </td>
                        <td className="border p-2 text-center">
                          {row.igstRate}
                        </td>
                        <td className="border p-2 text-center">
                          {row.sgstRate}
                        </td>
                        <td className="border p-2 text-center">
                          {row.receivedDate}
                        </td>
                        <td className="border p-2 text-center">
                          {row.expiryDate}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ==========================
   Enhanced Upload Modal
   --------------------------
   This component displays three tabs – CSV Upload, Gallery Upload,
   and Smart Scan – allowing the user to choose their preferred
   method. The “Smart Scan” tab is highlighted as a new, AI-powered feature.
   ========================== */
export const EnhancedUpload = ({ onClose, onUpload, isPublished }) => {
  const [selectedOption, setSelectedOption] = useState("csv");

  const renderContent = () => {
    if (selectedOption === "csv") {
      return (
        <UploadBatchModal
          onClose={onClose}
          onUpload={onUpload}
          isPublished={isPublished}
        />
      );
    } else if (selectedOption === "gallery") {
      return <DocumentScanner mode="gallery" />;
    } else if (selectedOption === "smart") {
      return <DocumentScanner mode="smart" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 w-full max-w-4xl">
        <div className="flex justify-between items-center border-b pb-3 mb-4">
          <h3 className="text-2xl font-semibold">Enhanced Upload</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ✖
          </button>
        </div>
        <div className="mb-4">
          <nav className="flex space-x-4">
            <button
              onClick={() => setSelectedOption("csv")}
              className={`px-4 py-2 rounded ${
                selectedOption === "csv"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-800"
              }`}
            >
              Upload from CSV
            </button>
            <button
              onClick={() => setSelectedOption("gallery")}
              className={`px-4 py-2 rounded ${
                selectedOption === "gallery"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-800"
              }`}
            >
              Upload from Gallery
            </button>
            <button
              onClick={() => setSelectedOption("smart")}
              className={`px-4 py-2 rounded ${
                selectedOption === "smart"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-800"
              }`}
            >
              Smart Scan{" "}
              <span className="ml-1 bg-red-500 text-white px-2 rounded text-xs">
                New
              </span>
            </button>
          </nav>
        </div>
        <div>{renderContent()}</div>
      </div>
    </div>
  );
};

export default EnhancedUpload;
